import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { uploadImage } from "@/lib/uploads";
import { fullProfileQuery, PRO_TYPES } from "@/lib/people";
import { useT } from "@/i18n";

export function ProProfilePanel({ userId }: { userId: string | undefined }) {
  const queryClient = useQueryClient();
  const { t } = useT();
  const { data: profile } = useQuery(fullProfileQuery(userId));
  const [name, setName] = useState("");
  const [type, setType] = useState("brand");
  const [website, setWebsite] = useState("");
  const [bio, setBio] = useState("");
  const [logo, setLogo] = useState("");
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!profile) return;
    setName(profile.pro_name ?? "");
    setType(profile.pro_type ?? "brand");
    setWebsite(profile.pro_website ?? "");
    setBio(profile.pro_bio ?? "");
    setLogo(profile.pro_logo_url ?? "");
  }, [profile]);

  async function handleLogo(file: File | undefined) {
    if (!file || !userId) return;
    try {
      const url = await uploadImage(file, userId, "pro-logo");
      setLogo(url);
      toast.success(t("account.pro.logoUploaded"));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("account.pro.uploadFailed"));
    }
  }

async function save(event: React.FormEvent) {
    event.preventDefault();
    if (!userId) return;
    setSaving(true);
    try {
      const alreadyPro = Boolean(profile?.pro_enabled);
      if (!alreadyPro) {
        const { activateProProfile, PRO_ACTIVATION_HEAT } = await import("@/lib/pro.functions");
        const res = await activateProProfile();
        if (res.ok && !res.already) {
          toast.success(t("account.pro.activated", { heat: PRO_ACTIVATION_HEAT }));
        }
      }

      const { error } = await supabase
        .from("profiles")
        .update({
          pro_enabled: true,
          pro_name: name.trim() || null,
          pro_type: type,
          pro_website: website.trim() || null,
          pro_bio: bio.trim() || null,
          pro_logo_url: logo || null,
        })
        .eq("id", userId);
      if (error) throw error;
      await queryClient.invalidateQueries({ queryKey: ["full-profile", userId] });
      toast.success(t("account.pro.saved"));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("account.pro.saveError"));
    } finally {
      setSaving(false);
    }
  }

  // A normal creator never sees professional fields: only an explicit opt-in
  // (or an already-activated professional account) opens the form.
  if (!profile?.pro_enabled && !open) {
    return (
      <section className="card-surface space-y-3 p-6">
        <h2 className="font-display text-xl font-bold">{t("account.pro.optinTitle")}</h2>
        <p className="text-sm text-muted-foreground">
          {t("account.pro.optinBody")}
        </p>
        <Button type="button" variant="outline" onClick={() => setOpen(true)}>
          {t("account.pro.optinCta")}
        </Button>
      </section>
    );
  }

  return (
    <form onSubmit={save} className="card-surface space-y-5 p-6">
      <div>
<h2 className="font-display text-xl font-bold">{t("account.pro.title")}</h2>
        <p className="text-sm text-muted-foreground">
          {t("account.pro.descA")}{" "}
          <span className="font-semibold text-foreground">{t("account.pro.descHeat")}</span>{" "}
          {t("account.pro.descB")}
        </p>
      </div>


      <div className="flex items-center gap-4">
        {logo ? (
          <img src={logo} alt="" className="size-16 rounded-xl object-cover" />
        ) : (
          <div className="flex size-16 items-center justify-center rounded-xl bg-accent text-xl font-bold text-accent-foreground">
            {(name || "?").charAt(0).toUpperCase()}
          </div>
        )}
        <div>
          <Label htmlFor="pro-logo" className="cursor-pointer">
            <span className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm font-medium hover:bg-muted">
              <Upload className="size-4" /> {t("account.pro.uploadLogo")}
            </span>
          </Label>
          <input
            id="pro-logo"
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => void handleLogo(e.target.files?.[0])}
          />
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="pro-name">{t("account.pro.name")}</Label>
          <Input id="pro-name" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="pro-type">{t("account.pro.type")}</Label>
          <select
            id="pro-type"
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="h-10 w-full rounded-lg border border-input bg-background px-3 text-sm"
          >
            {PRO_TYPES.map((pt) => (
              <option key={pt.value} value={pt.value}>
                {pt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="pro-site">{t("account.pro.website")}</Label>
        <Input
          id="pro-site"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          placeholder="https://"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="pro-bio">{t("account.pro.whatYouDo")}</Label>
        <Textarea id="pro-bio" rows={4} value={bio} onChange={(e) => setBio(e.target.value)} />
      </div>

<Button type="submit" variant="hot" disabled={saving}>
        {saving
          ? t("account.pro.saving")
          : profile?.pro_enabled
            ? t("account.pro.save")
            : t("account.pro.activate")}
      </Button>
    </form>
  );
}
